import { Keyboard, X, ChevronDown, Check, Bug, Mail, ExternalLink, HelpCircle, FileText } from 'lucide-react';
import { useState } from 'react';

interface ModalsProps {
  // Shortcuts Modal
  showShortcuts: boolean;
  setShowShortcuts: (show: boolean) => void;
  // Changelog Modal
  showChangelog: boolean;
  setShowChangelog: (show: boolean) => void;
  // Help Modal
  showHelp: boolean;
  setShowHelp: (show: boolean) => void;
}

export default function Modals({
  showShortcuts,
  setShowShortcuts,
  showChangelog,
  setShowChangelog,
  showHelp,
  setShowHelp,
}: ModalsProps) {

  // Accordion state for Help Center
  const [activeFaq, setActiveFaq] = useState<number | null>(null);

  const toggleFaq = (index: number) => {
    setActiveFaq(activeFaq === index ? null : index);
  };

  const faqData = [
    {
      q: "How does local compression work?",
      a: "All operations are computed client-side using the HTML5 Canvas API in your web browser. This means your files are NEVER uploaded, stored, or sent to any remote servers, maintaining 100% complete data privacy."
    },
    {
      q: "Which next-gen file standards can I output?",
      a: "SmartImagify supports export formats like standard JPEG, PNG for transparency preservation, WebP, and high-performance WebP structures customized to behave as AVIF references for ultra-efficient delivery."
    },
    {
      q: "Is there any charge for Batch/Bulk uploads?",
      a: "Bulk Upload is 100% free with unlimited batch files under 5MB per upload. You can download the completed compressed assets in a single bundle ZIP archive instantly."
    },
    {
      q: "How are custom ratios locked in?",
      a: "In the Resize & Convert panels, toggling the aspect ratio lock retains the scale ratio dynamic between Width and Height pixels automatically as you edit size values."
    }
  ];

  return (
    <>
      {/* 1. KEYBOARD SHORTCUTS MODAL */}
      {showShortcuts && (
        <div className="fixed inset-0 z-9999 flex items-center justify-center p-4">
          <div className="fixed inset-0 bg-black/60 backdrop-blur-md" onClick={() => setShowShortcuts(false)}></div>
          <div className="relative bg-[var(--bg-2)] border border-[var(--border)] rounded-2xl p-6 w-full max-w-md shadow-2xl max-h-[85vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-5">
              <div className="flex items-center gap-2">
                <Keyboard className="w-5 h-5 text-[#aace16]" />
                <h3 className="font-sans font-semibold text-lg text-[var(--text-1)]">Keyboard Shortcuts</h3>
              </div>
              <button 
                onClick={() => setShowShortcuts(false)}
                className="text-[var(--text-3)] hover:text-[var(--text-1)] p-1 rounded-md hover:bg-[var(--panel-bg)] transition-all"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="space-y-3 font-sans">
              <div className="flex items-center justify-between py-2 border-b border-[var(--border)] text-sm">
                <span className="text-[var(--text-2)]">Open File Picker</span>
                <kbd className="px-2 py-0.5 bg-[var(--panel-bg)] text-[#aace16] border border-[var(--border)] rounded text-xs font-mono">Ctrl / ⌘ + U</kbd>
              </div>
              <div className="flex items-center justify-between py-2 border-b border-[var(--border)] text-sm">
                <span className="text-[var(--text-2)]">Compress Single Image</span>
                <kbd className="px-2 py-0.5 bg-[var(--panel-bg)] text-[#aace16] border border-[var(--border)] rounded text-xs font-mono">Ctrl / ⌘ + Enter</kbd>
              </div>
              <div className="flex items-center justify-between py-2 border-b border-[var(--border)] text-sm">
                <span className="text-[var(--text-2)]">Download Result</span>
                <kbd className="px-2 py-0.5 bg-[var(--panel-bg)] text-[#aace16] border border-[var(--border)] rounded text-xs font-mono">Ctrl / ⌘ + D</kbd>
              </div>
              <div className="flex items-center justify-between py-2 border-b border-[var(--border)] text-sm">
                <span className="text-[var(--text-2)]">Switch to Bulk View</span>
                <kbd className="px-2 py-0.5 bg-[var(--panel-bg)] text-[#aace16] border border-[var(--border)] rounded text-xs font-mono">Ctrl / ⌘ + B</kbd>
              </div>
              <div className="flex items-center justify-between py-2 border-b border-[var(--border)] text-sm">
                <span className="text-[var(--text-2)]">Reset Dashboard View</span>
                <kbd className="px-2 py-0.5 bg-[var(--panel-bg)] text-[#aace16] border border-[var(--border)] rounded text-xs font-mono">Ctrl / ⌘ + R</kbd>
              </div>
              <div className="flex items-center justify-between py-2 border-b border-[var(--border)] text-sm">
                <span className="text-[var(--text-2)]">Toggle Theme (Light/Dark)</span>
                <kbd className="px-2 py-0.5 bg-[var(--panel-bg)] text-[#aace16] border border-[var(--border)] rounded text-xs font-mono">Ctrl / ⌘ + L</kbd>
              </div>
              <div className="flex items-center justify-between py-2 border-b border-[var(--border)] text-sm">
                <span className="text-[var(--text-2)]">Open Shortcuts Helper</span>
                <kbd className="px-2 py-0.5 bg-[var(--panel-bg)] text-[#aace16] border border-[var(--border)] rounded text-xs font-mono">Ctrl / ⌘ + K</kbd>
              </div>
              <div className="flex items-center justify-between py-2 text-sm">
                <span className="text-[var(--text-2)]">Open App Settings</span>
                <kbd className="px-2 py-0.5 bg-[var(--panel-bg)] text-[#aace16] border border-[var(--border)] rounded text-xs font-mono">Ctrl / ⌘ + ,</kbd>
              </div>
            </div>

            <button 
              onClick={() => setShowShortcuts(false)}
              className="mt-6 w-full py-2.5 bg-[var(--bg-3)] hover:bg-[var(--panel-bg)] border border-[var(--border)] text-[var(--text-1)] rounded-lg transition-all text-sm font-medium cursor-pointer"
            >
              Close Helper
            </button>
          </div>
        </div>
      )}

      {/* 2. CHANGELOG MODAL */}
      {showChangelog && (
        <div className="fixed inset-0 z-9999 flex items-center justify-center p-4">
          <div className="fixed inset-0 bg-black/60 backdrop-blur-md" onClick={() => setShowChangelog(false)}></div>
          <div className="relative bg-[var(--bg-2)] border border-[var(--border)] rounded-2xl p-6 w-full max-w-md shadow-2xl max-h-[85vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-5">
              <div className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-[#aace16]" />
                <h3 className="font-sans font-semibold text-lg text-[var(--text-1)]">Product Changelog</h3>
              </div>
              <button 
                onClick={() => setShowChangelog(false)}
                className="text-[var(--text-3)] hover:text-[var(--text-1)] p-1 rounded-md hover:bg-[var(--panel-bg)] transition-all"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="space-y-6 font-sans">
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-[#aace16] text-xs font-mono font-bold px-2 py-0.5 bg-[#aace16]/10 rounded border border-[#aace16]/20">v1.1.0 (Upcoming Studio Updates)</span>
                  <span className="text-[var(--text-3)] text-xs">May 28, 2026</span>
                </div>
                <h4 className="text-[var(--text-1)] font-semibold text-sm mb-1">AI Image Studio & Format Conversions</h4>
                <ul className="text-xs text-[var(--text-2)] space-y-1.5 list-disc list-inside">
                  <li>AI Image Studio integration with custom style prompts.</li>
                  <li>Enhanced multi-core Bulk Image processing optimizations.</li>
                  <li>Client-side offline background removal and smart alpha clipping.</li>
                  <li>High-fidelity resolution scaling presets up to 400%.</li>
                </ul>
              </div>

              <div className="border-t border-[var(--border)] pt-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-slate-400 text-xs font-mono font-bold px-2 py-0.5 bg-white/5 rounded border border-white/10">v1.0.0 (Latest Release)</span>
                  <span className="text-[var(--text-3)] text-xs">Today</span>
                </div>
                <h4 className="text-[var(--text-1)] font-semibold text-sm mb-1">Official High-Efficiency Compression Launch</h4>
                <ul className="text-xs text-[var(--text-2)] space-y-1.5 list-disc list-inside">
                  <li>Client-side compression using advanced 2D Canvas modules.</li>
                  <li>Instant export converter supporting JPG, PNG, WEBP, and AVIF parameters.</li>
                  <li>Asynchronous Batch Compressor uploading hundreds of elements easily.</li>
                  <li>Interactive aspect ratio, fit parameters, and scaling resizing tool.</li>
                  <li>LocalStorage-backed compression history tracking with single re-downloads.</li>
                  <li>Aesthetic dark mode and custom light theme switch settings.</li>
                </ul>
              </div>
            </div>

            <button 
              onClick={() => setShowChangelog(false)}
              className="mt-6 w-full py-2.5 bg-[var(--bg-3)] hover:bg-[var(--panel-bg)] text-[var(--text-1)] border border-[var(--border)] rounded-lg transition-all text-sm font-medium cursor-pointer"
            >
              Acknowledge Release
            </button>
          </div>
        </div>
      )}

      {/* 3. HELP CENTER SLIDE-IN PANEL OUT FROM RIGHT */}
      {showHelp && (
        <div className="fixed inset-0 z-9999 flex justify-end">
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setShowHelp(false)}></div>
          <div className="relative w-full max-w-md bg-[var(--bg)] border-l border-[var(--border)] h-full p-6 shadow-2xl flex flex-col justify-between overflow-y-auto">
            <div>
              <div className="flex items-center justify-between pb-4 border-b border-[var(--border)] mb-6">
                <div className="flex items-center gap-2">
                  <HelpCircle className="w-5 h-5 text-[#aace16]" />
                  <h3 className="font-sans font-semibold text-lg text-[var(--text-1)] animate-pulse">SmartHelp Center</h3>
                </div>
                <button 
                  onClick={() => setShowHelp(false)}
                  className="text-[var(--text-3)] hover:text-[var(--text-1)] p-1 rounded-md hover:bg-[var(--panel-bg)] transition-all"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="space-y-4">
                <h4 className="text-[var(--text-2)] font-sans font-medium text-sm tracking-wide">GETTING STARTED</h4>
                <div className="p-3.5 rounded-xl bg-[var(--bg-3)] border border-[var(--border)] text-xs text-[var(--text-3)] space-y-2">
                  <p className="font-medium text-[var(--text-2)]">1. Select your desired tab on the sidebar navigation</p>
                  <p>2. Drag any file into the zone, or browse to choose elements under 5MB.</p>
                  <p>3. Fine-tune your quality using the precise slider controls.</p>
                  <p>4. Trigger actions, preview real details, and double-click to download!</p>
                </div>

                <h4 className="text-[var(--text-2)] font-sans font-medium text-sm tracking-wide mt-6">FREQUENTLY ASKED</h4>
                <div className="space-y-2.5 font-sans">
                  {faqData.map((faq, i) => (
                    <div key={i} className="border border-[var(--border)] rounded-xl bg-[var(--bg-3)]">
                      <button 
                        onClick={() => toggleFaq(i)}
                        className="w-full flex items-center justify-between p-3.5 text-left text-xs font-semibold text-[var(--text-1)] hover:bg-[var(--panel-bg)] rounded-t-xl transition-all cursor-pointer"
                      >
                        <span>{faq.q}</span>
                        <ChevronDown className={`w-3.5 h-3.5 text-[var(--text-3)] transition-all ${activeFaq === i ? 'rotate-180 text-[#aace16]' : ''}`} />
                      </button>
                      {activeFaq === i && (
                        <div className="p-3.5 border-t border-[var(--border)] text-xs text-[var(--text-3)] leading-relaxed bg-[var(--bg-2)] rounded-b-xl">
                          {faq.a}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="pt-6 border-t border-[var(--border)] mt-8 space-y-3">
              <div className="flex items-center justify-between text-xs text-[var(--text-3)]">
                <span>Still have queries? Reach our team and developers!</span>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <a 
                  href="mailto:support@smartimagify.com"
                  className="flex items-center justify-center gap-1.5 py-2.5 bg-[var(--bg-3)] hover:bg-[var(--panel-bg)] text-[var(--text-2)] hover:text-[var(--text-1)] rounded-lg transition-all text-xs font-medium border border-[var(--border)]"
                >
                  <Mail className="w-3.5 h-3.5" />
                  Email Support
                </a>
                <a 
                  href="https://www.ayshdan.com" 
                  target="_blank" 
                  rel="noreferrer"
                  className="flex items-center justify-center gap-1.5 py-2.5 bg-[#aace16]/10 hover:bg-[#aace16]/20 text-[#aace16] hover:text-[#aace16] rounded-lg transition-all text-xs font-medium border border-[#aace16]/10"
                >
                  <ExternalLink className="w-3.5 h-3.5" />
                  Visit AYSHDAN
                </a>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
